import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-chronic-endometritis-testing"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "Chronic Endometritis Testing",
  subtitle: "Biopsy-based workup for uterine inflammation or infection",
  icon: "🧪",
  body: {
    testName: "Chronic Endometritis Testing",
    category: "biopsy",
    whatItMeasures:
      "Looks for evidence of chronic endometritis in an endometrial biopsy sample.\n\nThis can be evaluated with histology, CD138 staining, hysteroscopy findings, culture, PCR, or NGS-based panels.\n\nALICE is the Igenomix-branded molecular version focused on bacteria associated with chronic endometritis.",
    howItWorks:
      "A clinician takes an endometrial biopsy. Depending on the test ordered, the lab may look for plasma cells with histology or CD138 staining, culture organisms, or use molecular methods such as PCR or NGS to detect bacteria. The result may lead to antibiotics, probiotics, repeat testing, or a different uterine workup, but it should not replace clinician-led evaluation.",
    howItWorksSteps: [
      "A clinician takes an endometrial biopsy with a thin catheter.",
      "The lab checks for chronic endometritis using the method ordered: histology, CD138 staining, culture, PCR, NGS, or a branded panel such as ALICE.",
      "Biopsy timing depends on whether the chronic endometritis workup is standalone or bundled with receptivity testing; confirm mock-cycle timing with the clinic.",
      "The result may lead to antibiotics or probiotics before transfer, but it should not replace clinician-led evaluation for chronic endometritis or uterine pathology.",
    ],
    quickFacts: [
      { kind: "sample", value: "Endometrial tissue biopsy" },
      { kind: "time", value: "5-10 min biopsy" },
      { kind: "pain", value: "Cramping during and shortly after" },
      { kind: "anesthesia", value: "Usually none" },
      { kind: "prep", value: "No fasting usually; ask about ibuprofen" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: [
      {
        label: "Not detected",
        meaning:
          "The report does not detect the chronic-endometritis-associated bacteria included in the panel.",
        whatToExpect:
          "This is reassuring for the organisms tested, but it does not rule out every uterine factor, every infection, or non-infectious causes of implantation problems.",
      },
      {
        label: "Detected",
        meaning:
          "The report detects one or more bacteria associated with chronic endometritis.",
        whatToExpect:
          "The clinic may discuss antibiotics, probiotics, or follow-up testing. A detected organism is not automatically actionable without clinical context.",
      },
      {
        label: "Inconclusive",
        meaning:
          "The sample or sequencing result is not clear enough to make a confident call.",
        whatToExpect:
          "Your clinic may recommend repeating the biopsy or using a different chronic endometritis workup, such as histology, CD138 staining, hysteroscopy findings, culture, or PCR.",
      },
    ],
    turnaroundTime: "2-3 weeks",
    costRange: { low: 300, high: 700 },
    atHome: false,
    clinicRequired: true,
    evidenceStrength: "debunked",
    headlineStat: "No RCT-proven benefit",
    expectations: {
      canExpect: [
        "A biopsy-based report focused on chronic endometritis, inflammation, or chronic endometritis-associated organisms",
        "Possible antibiotic treatment if bacteria are detected",
        "May be discussed after recurrent implantation failure, recurrent loss, or symptoms suggesting uterine inflammation",
      ],
      shouldNotExpect: [
        "Routine chronic endometritis screening with molecular panels has not been shown to improve live birth for most IVF patients",
        "It should not replace a clinician-led workup of chronic endometritis or uterine pathology",
        "A detected organism is not automatically actionable without clinical context",
      ],
    },
    considerations: [
      "Ask whether chronic endometritis is suspected clinically or whether testing is being sold as a blanket add-on",
      "Ask which method is being used: histology, CD138 staining, hysteroscopy findings, culture, PCR, NGS, or a branded panel",
      "Antibiotics can have side effects and disrupt the microbiome, so treatment should have a clear reason",
      "A negative result does not rule out every uterine factor affecting implantation",
    ],
    bestFor: [
      "Suspected chronic endometritis under clinician guidance",
      "Selected recurrent implantation failure or recurrent loss workup",
      "Not recommended as a routine IVF add-on",
    ],
    keyFindings: [
      {
        direction: "negative",
        outcome:
          "NICE found no eligible evidence that microbiological analysis tests such as ALICE improve fertility treatment outcomes",
        mechanism:
          "Finding bacteria associated with endometritis is not the same as proving that a commercial test-and-treat pathway improves live birth",
        source: "NICE NG257 evidence review, 2026",
      },
      {
        direction: "neutral",
        outcome:
          "Chronic endometritis itself can matter clinically, but the best diagnostic pathway and fertility benefit of commercial sequencing panels remain unsettled",
        source: "ESHRE recurrent implantation failure recommendations, 2023",
      },
      {
        direction: "negative",
        outcome:
          "Routine testing can add biopsy risk, antibiotics, cost, and transfer delay without proven outcome benefit",
        source: "NICE NG257 evidence review, 2026",
      },
    ],
    soldAs: [
      {
        name: "ALICE",
        company: "Igenomix",
        description:
          "A branded molecular chronic endometritis test that looks for bacteria associated with chronic endometritis in an endometrial biopsy sample.",
        evidenceSummary:
          "The product can report detected organisms, but independent guidance has not found evidence that routine commercial microbiological add-on testing improves fertility outcomes.",
        linkedTestId: makeId(
          "test-chronic-endometritis-testing-products-alice",
        ),
      },
    ],
    dataPoints: [],
    section: "implantation",
  },
  sources: [
    {
      title:
        "Fertility problems: assessment and treatment, evidence review D, endometrial receptivity testing",
      journal: "NICE NG257",
      year: 2026,
      url: "https://www.nice.org.uk/guidance/ng257/evidence/d-endometrial-receptivity-testing-pdf-15664473329",
      evidenceLevel: "guideline",
    },
    {
      title: "ALICE Analysis of Infectious Chronic Endometritis",
      journal: "Igenomix product information",
      year: 2026,
      url: "https://www.igenomix.eu/our-services/alice-analysis-of-infectious-chronic-endometritis/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Good practice recommendations on recurrent implantation failure: an evidence-based guideline",
      authors:
        "ESHRE Working Group on Recurrent Implantation Failure (Cimadomo D et al.)",
      journal: "Human Reproduction Open",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37325540/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "The diagnosis of chronic endometritis in infertile asymptomatic women: a comparative study of histology, microbial cultures, hysteroscopy, and molecular microbiology",
      authors: "Moreno I et al.",
      journal: "American Journal of Obstetrics & Gynecology",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/30240652/",
      evidenceLevel: "cohort",
    },
    {
      title:
        "Good practice recommendations on add-ons in reproductive medicine",
      authors: "ESHRE Add-ons working group; Lundin K, Bentzen JG, et al.",
      journal: "Human Reproduction",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37747409/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);

const aliceProduct: ContentBlock<"test"> = {
  id: makeId("test-chronic-endometritis-testing-products-alice"),
  kind: "test",
  tags: ["testing", "her", "ivf", "fet", "pre-cycle"],
  title: "ALICE",
  subtitle: "Commercial test profile for chronic endometritis testing",
  icon: "🧪",
  body: {
    testName: "ALICE (Analysis of Infectious Chronic Endometritis)",
    category: "biopsy",
    evidenceStrength: "debunked",
    isCommercialProductProfile: true,
    commercialProductProfile: {
      categoryTestId: makeId("test-chronic-endometritis-testing"),
      company: "Igenomix",
      productUrl:
        "https://www.igenomix.eu/our-services/alice-analysis-of-infectious-chronic-endometritis/",
      marketedNames: ["ALICE", "Analysis of Infectious Chronic Endometritis"],
      availability: [
        {
          region: "Patient access",
          detail:
            "Usually ordered through a fertility clinic after an endometrial biopsy; availability depends on the clinic and Igenomix lab route.",
        },
      ],
    },
    whatItMeasures:
      "ALICE is the Igenomix-branded molecular version of chronic endometritis testing. It looks for bacteria associated with chronic endometritis in an endometrial biopsy sample.",
    howItWorks: test.body.howItWorks,
    howItWorksSteps: test.body.howItWorksSteps,
    quickFacts: test.body.quickFacts,
    resultTypes: test.body.resultTypes,
    expectations: test.body.expectations,
    considerations: [
      "Ask whether chronic endometritis is clinically suspected or whether ALICE is being sold as a routine add-on.",
      "Ask whether the clinic would confirm the finding with histology, CD138 staining, hysteroscopy findings, culture, or another workup.",
      "Ask exactly what antibiotic or follow-up plan would change if ALICE is positive.",
    ],
    bestFor: test.body.bestFor,
    keyFindings: test.body.keyFindings,
    headlineStat: test.body.headlineStat,
    turnaroundTime: test.body.turnaroundTime,
    costRange: test.body.costRange,
    atHome: false,
    clinicRequired: true,
    dataPoints: [],
    section: "implantation",
  },
  sources: test.sources,
  readTimeMinutes: 4,
};

registerBlock(aliceProduct);

registerRelationship({
  from: makeId("test-chronic-endometritis-testing"),
  to: makeId("test-chronic-endometritis-testing-products-alice"),
  type: "related-to",
  label: "Sold as",
});
